import type { StateCreator } from 'zustand';

import { getWardrobeCategoriesByGender } from '@/constants/generated';
import { mergeUserItemsIntoCategories } from '@/services/userItemsStorage';

import { getUserUploadedItems } from './helpers';
import type { IWardrobeStore } from './types';

export const initialGeneration: IWardrobeStore['generation'] = {
  isGenerating: false,
  isViewingGeneration: false,
  activeGenerationCount: 0,
  latestGenerationId: 0,
  progress: 0,
  currentResult: null,
  variants: [],
  history: [],
  error: null,
  viewMode: 'standard' as const,
  viewedHistoryItem: null,
};

/**
 * Generation slice — in-flight + completed generation state and view mode.
 *
 * Cross-slice writes:
 * - `generationSuccess` bumps `ui.unseenHistoryCount` when a result lands
 *   while the user is viewing a different pending generation.
 * - `viewHistoryItem` is the big one: when restoring a saved look it
 *   replaces character + wardrobe state in addition to generation. Stays
 *   here because the trigger is "view this generation".
 */
export type GenerationSliceContribution = Pick<
  IWardrobeStore,
  | 'generation'
  | 'setViewMode'
  | 'startGeneration'
  | 'setGenerationProgress'
  | 'generationSuccess'
  | 'setCurrentResult'
  | 'generationVariantsSuccess'
  | 'generationError'
  | 'clearGenerationError'
  | 'clearCurrentResult'
  | 'clearHistory'
  | 'viewHistoryItem'
>;

export const createGenerationSlice: StateCreator<IWardrobeStore, [], [], GenerationSliceContribution> = set => ({
  generation: initialGeneration,

  setViewMode: mode =>
    set(s => ({
      generation: {
        ...s.generation,
        viewMode: mode,
        viewedHistoryItem: mode === 'grid' ? s.generation.viewedHistoryItem : null,
      },
    })),

  startGeneration: () =>
    set(s => ({
      generation: {
        ...s.generation,
        isGenerating: true,
        isViewingGeneration: true,
        activeGenerationCount: s.generation.activeGenerationCount + 1,
        latestGenerationId: s.generation.latestGenerationId + 1,
        progress: 0,
        error: null,
      },
    })),

  setGenerationProgress: progress => set(s => ({ generation: { ...s.generation, progress } })),

  generationSuccess: (result, generationId, characterId) =>
    set(s => {
      const stampedResult = characterId ? { ...result, characterId } : result;
      const remaining = Math.max(0, s.generation.activeGenerationCount - 1);
      const stillPending = remaining > 0 || s.library.pendingGenerations.length > 0;
      const isLatest = !generationId || generationId === s.generation.latestGenerationId;
      const isViewingOtherPending = s.library.activePendingId !== null;
      // If the user navigated away from the in-flight generation, don't yank
      // the canvas back to the new result — keep their view and surface the
      // completion via the unseen-history badge instead.
      //
      // Invariant: `isViewingGeneration` is true iff the user is actively
      // waiting for an in-flight job. startGeneration / viewPendingGeneration
      // set it true; selectLook / viewHistoryItem / detachFromGeneration /
      // selectCharacter clear it. So `!isViewingGeneration` at completion
      // time means the user navigated somewhere. The activeLookId /
      // viewedHistoryItem AND-clause is a defensive guard so a stray
      // false-positive doesn't suppress display.
      const hasNavigatedAway = !s.generation.isViewingGeneration && (s.library.activeLookId !== null || s.generation.viewedHistoryItem !== null);
      const shouldDisplay = isLatest && !isViewingOtherPending && !hasNavigatedAway;
      const newHistory = [stampedResult, ...s.generation.history].slice(0, 8);
      return {
        generation: {
          ...s.generation,
          activeGenerationCount: remaining,
          isGenerating: stillPending,
          isViewingGeneration: shouldDisplay ? false : s.generation.isViewingGeneration,
          progress: shouldDisplay ? 100 : s.generation.progress,
          currentResult: shouldDisplay ? stampedResult : s.generation.currentResult,
          history: newHistory,
          viewedHistoryItem: shouldDisplay ? null : s.generation.viewedHistoryItem,
          error: shouldDisplay ? null : s.generation.error,
        },
        ui: shouldDisplay ? s.ui : { ...s.ui, unseenHistoryCount: s.ui.unseenHistoryCount + 1 },
      };
    }),

  setCurrentResult: result =>
    set(s => ({
      generation: { ...s.generation, currentResult: result },
    })),

  generationVariantsSuccess: variants =>
    set(s => ({
      generation: {
        ...s.generation,
        isGenerating: false,
        variants,
        error: null,
      },
    })),

  generationError: message =>
    set(s => {
      const remaining = Math.max(0, s.generation.activeGenerationCount - 1);
      const stillPending = remaining > 0 || s.library.pendingGenerations.length > 0;
      return {
        generation: {
          ...s.generation,
          activeGenerationCount: remaining,
          isGenerating: stillPending,
          isViewingGeneration: stillPending,
          error: message,
        },
      };
    }),

  clearGenerationError: () =>
    set(s => ({
      generation: { ...s.generation, error: null },
    })),

  clearCurrentResult: () => {
    set(s => ({
      generation: {
        ...s.generation,
        currentResult: null,
        variants: [],
        error: null,
      },
    }));
  },

  clearHistory: () => {
    set(s => ({ generation: { ...s.generation, history: [] } }));
  },

  viewHistoryItem: item => {
    if (!item) {
      set(s => ({ generation: { ...s.generation, viewedHistoryItem: null } }));
      return;
    }

    set(s => {
      // If the history item belongs to a saved library character, sync the
      // library highlight so it doesn't point at a different (or stale) entry.
      const ownerCharacter = s.library.characters.find(c => c.looks.some(l => l.id === item.id));
      const reqChar = item.request.character;
      const genderChanged = reqChar.gender !== s.character.gender;
      const nextCategories = genderChanged ? mergeUserItemsIntoCategories(getWardrobeCategoriesByGender(reqChar.gender), getUserUploadedItems(s.wardrobe.categories)) : s.wardrobe.categories;

      return {
        character: {
          ...s.character,
          imageUrl: reqChar.imageUrl,
          imageFile: null,
          imageAspectRatio: null,
          gender: reqChar.gender,
          selectedStyle: reqChar.selectedStyle,
          selectedPose: reqChar.selectedPose,
          selectedBackground: reqChar.selectedBackground,
          selectedHairstyle: reqChar.selectedHairstyle,
          selectedHairColor: reqChar.selectedHairColor,
          selectedRetouchPresets: reqChar.selectedRetouchPresets ?? {},
        },
        wardrobe: {
          ...s.wardrobe,
          categories: nextCategories,
          selectedItems: item.request.wardrobeItems,
        },
        // isViewingGeneration: false because the user has navigated to a
        // history item — they're no longer "waiting for the in-flight
        // generation". Matches selectLook's behavior and is what
        // generationSuccess's hasNavigatedAway check looks for.
        generation: { ...s.generation, viewedHistoryItem: item, isViewingGeneration: false },
        library: {
          ...s.library,
          activeCharacterId: ownerCharacter ? ownerCharacter.id : s.library.activeCharacterId,
          activeLookId: ownerCharacter ? item.id : s.library.activeLookId,
          activePendingId: null,
        },
      };
    });
  },
});
